import React, { useState, useMemo, useEffect } from 'react';
import { Radio, Select, Input, type RadioChangeEvent } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { FormattedMessage, useIntl } from 'react-intl';
import type { IPickupDetails, Question } from '@common-types';
import { findTransferArrivalModeQuestion, getAllowedPickupTypesForSelect, filterPickupsForLocationSelect, filterOtherPickupRadios, isPickupReferenceAllowed, normPickupType } from '@share/utils';

const FREETEXT_RADIO = '__freetext__';
const U_LOCATION_REF = 'LOCATION_REFERENCE';
const U_FREETEXT = 'FREETEXT';

/** Set from checkout question tree: which mode (arrival vs departure) governs this location field. */
type TransferPickupBranch = 'arrival' | 'departure' | null;

interface PickupQuestionProps {
    travelerPickups: IPickupDetails[];
    allowCustomTravelerPickup?: boolean;
    bookingAnswers?: Record<string, { value?: unknown; unit?: string }>;
    bookingQuestions?: Question[];
    /** When false, pickup uses legacy rules (e.g. transfer question not yet in visible checkout list). */
    transferArrivalGatesPickup?: boolean;
    /**
     * Arrival subtree → use only `TRANSFER_ARRIVAL_MODE` for allowed locations.
     * Departure subtree → use only `TRANSFER_DEPARTURE_MODE`.
     * `null` = not under a mode node (rare); falls back to arrival, then departure.
     */
    transferPickupBranch?: TransferPickupBranch;
    /** Question `units` from the product; if it includes `FREETEXT`, show the manual entry radio. */
    questionUnits?: string[];
    /** Current answer unit (e.g. `LOCATION_REFERENCE` or `FREETEXT`). */
    valueUnit?: string;
    value: any;
    onChange: (value: any, unit: string) => void;
}

export const PickupQuestion: React.FC<PickupQuestionProps> = ({
    travelerPickups,
    allowCustomTravelerPickup,
    bookingAnswers,
    bookingQuestions,
    transferArrivalGatesPickup,
    transferPickupBranch = null,
    questionUnits,
    valueUnit,
    value,
    onChange,
}) => {
    const intl = useIntl();
    const [selectedOption, setSelectedOption] = useState<string | undefined>(value);

    const transferArrivalQuestion = useMemo(
        () => findTransferArrivalModeQuestion(bookingQuestions),
        [bookingQuestions]
    );
    const applyTransferRules =
        transferArrivalGatesPickup !== undefined
            ? transferArrivalGatesPickup
            : Boolean(transferArrivalQuestion);
    const transferArrivalRaw = bookingAnswers?.TRANSFER_ARRIVAL_MODE?.value;
    const transferDepartureRaw = bookingAnswers?.TRANSFER_DEPARTURE_MODE?.value;

    const trimAnswer = (v: unknown) =>
        v === undefined || v === null ? '' : String(v).trim();

    /**
     * Allowed locations follow `getAllowedPickupTypesForSelect(mode, ...)` for **this** subtree only:
     * arrival flow reads `TRANSFER_ARRIVAL_MODE`, departure flow reads `TRANSFER_DEPARTURE_MODE`, so
     * changing one mode does not reshape the other flow's pickup/dropoff lists.
     */
    const transferAnswerForPickup = (() => {
        const arr = trimAnswer(transferArrivalRaw);
        const dep = trimAnswer(transferDepartureRaw);
        if (transferPickupBranch === 'departure') {
            return dep !== '' ? dep : undefined;
        }
        if (transferPickupBranch === 'arrival') {
            return arr !== '' ? arr : undefined;
        }
        if (arr !== '') return arr;
        if (dep !== '') return dep;
        return undefined;
    })();
    const transferAnsweredForPickup = Boolean(transferAnswerForPickup);

    const allowedPickupTypes = useMemo(() => {
        if (!applyTransferRules || !transferAnsweredForPickup) {
            return null;
        }
        return getAllowedPickupTypesForSelect(transferAnswerForPickup, allowCustomTravelerPickup, travelerPickups);
    }, [
        applyTransferRules,
        transferAnsweredForPickup,
        transferAnswerForPickup,
        transferPickupBranch,
        allowCustomTravelerPickup,
        travelerPickups,
    ]);

    const filteredPoints = useMemo(
        () => filterPickupsForLocationSelect(travelerPickups, allowedPickupTypes),
        [travelerPickups, allowedPickupTypes]
    );
    const showLocationReferenceList = filteredPoints.length > 0;

    const filteredothersPoints = useMemo(
        () => filterOtherPickupRadios(travelerPickups, applyTransferRules ? transferArrivalQuestion : undefined),
        [travelerPickups, transferArrivalQuestion, applyTransferRules]
    );

    const pickupSelectDisabled = applyTransferRules && !transferAnsweredForPickup;
    const showFreetextOption = useMemo(
        () => (questionUnits || []).map((u) => String(u).toUpperCase()).includes(U_FREETEXT),
        [questionUnits]
    );
    const unit = (valueUnit || '').toUpperCase();

    useEffect(() => {
        if (showLocationReferenceList) {
            return;
        }
        if (selectedOption === 'pickup') {
            setSelectedOption(undefined);
        }
        if (unit === U_LOCATION_REF && value) {
            const inOtherOptions = filteredothersPoints.some((p) => p.reference === value);
            if (!inOtherOptions) {
                onChange(undefined, U_LOCATION_REF);
            }
        }
    }, [showLocationReferenceList, selectedOption, unit, value, filteredothersPoints, onChange]);

    useEffect(() => {
        if (pickupSelectDisabled && (value != null && value !== '')) {
            if (unit === U_FREETEXT) {
                onChange(undefined, U_LOCATION_REF);
                return;
            }
            const match = travelerPickups.find((p) => p.reference === value);
            if (match && normPickupType(match) !== 'OTHER') {
                onChange(undefined, U_LOCATION_REF);
            }
        }
    }, [pickupSelectDisabled, value, valueUnit, unit, travelerPickups, onChange]);

    useEffect(() => {
        if (!value || pickupSelectDisabled) {
            return;
        }
        if (unit === U_FREETEXT) {
            return;
        }
        if (typeof value !== 'string') {
            return;
        }
        if (
            !isPickupReferenceAllowed(
                value,
                travelerPickups,
                allowedPickupTypes,
                applyTransferRules ? transferArrivalQuestion : undefined
            )
        ) {
            onChange(undefined, U_LOCATION_REF);
        }
    }, [
        value,
        valueUnit,
        unit,
        pickupSelectDisabled,
        travelerPickups,
        allowedPickupTypes,
        transferArrivalQuestion,
        applyTransferRules,
        transferAnswerForPickup,
        transferPickupBranch,
        onChange,
    ]);

    useEffect(() => {
        if (unit === U_FREETEXT) {
            setSelectedOption(FREETEXT_RADIO);
            return;
        }
        if (value === undefined || value === null || value === '') {
            setSelectedOption(undefined);
            return;
        }
        if (value === 'pickup') {
            setSelectedOption('pickup');
            return;
        }
        const isPickup = showLocationReferenceList && filteredPoints.some(p => p.reference === value);
        if (isPickup) {
            setSelectedOption('pickup');
            return;
        }
        const isOther = filteredothersPoints.some(p => p.reference === value);
        if (isOther) {
            setSelectedOption(value);
        }
    }, [value, valueUnit, unit, filteredPoints, filteredothersPoints, showLocationReferenceList]);

    const handleOptionChange = (e: RadioChangeEvent) => {
        const option = e.target.value;
        setSelectedOption(option);
        if (option === 'pickup') {
            onChange(undefined, U_LOCATION_REF);
        } else if (option === FREETEXT_RADIO) {
            onChange(unit === U_FREETEXT && typeof value === 'string' ? value : '', U_FREETEXT);
        } else {
            onChange(option, U_LOCATION_REF);
        }
    };

    const freetextValue = unit === U_FREETEXT && typeof value === 'string' ? value : '';

    return (
        <div className="pickup-question-card" style={{ border: '1px solid #d9d9d9', borderRadius: '8px', padding: '16px' }}>
            <Radio.Group onChange={handleOptionChange} value={selectedOption} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {showLocationReferenceList && (
                    <div className="option-item" style={{ border: selectedOption === 'pickup' ? '1px solid #008489' : '1px solid #d9d9d9', borderRadius: '8px', textAlign: 'left', padding: '12px', backgroundColor: selectedOption === 'pickup' ? 'transparent' : 'transparent' }}>
                        <Radio value="pickup">
                            <span style={{ fontWeight: 500, color: '#262626' }}><FormattedMessage id="checkout.pickup.option" defaultMessage="I'd like to be picked up" /></span>
                        </Radio>
                        {selectedOption === 'pickup' && (
                            <div style={{ marginTop: '12px' }}>
                                <Select
                                    showSearch
                                    disabled={pickupSelectDisabled}
                                    style={{ width: '100%', height: 'auto' }}
                                    placeholder={intl.formatMessage({ id: 'checkout.pickup.search', defaultMessage: 'Search location' })}
                                    optionFilterProp="searchValue"
                                    onChange={(val) => onChange(val, U_LOCATION_REF)}
                                    value={filteredPoints.some(p => p.reference === value) ? value : undefined}
                                    suffixIcon={<SearchOutlined />}
                                    options={filteredPoints.map(p => ({
                                        value: p.reference,
                                        searchValue: p.description,
                                        label: (
                                            <div style={{ display: 'flex', flexDirection: 'column', gap: '7px', padding: '7px 0' }}>
                                                <div style={{ fontWeight: 500, lineHeight: '1' }}>{p.description ? p.description : p.reference}</div>
                                                <div style={{ fontSize: '12px', color: '#666', lineHeight: '1' }}>{p.address}</div>
                                            </div>
                                        ),
                                    }))}
                                />
                            </div>
                        )}
                    </div>
                )}

                {showFreetextOption && (
                    <div
                        className="option-item"
                        style={{
                            border: selectedOption === FREETEXT_RADIO ? '1px solid #008489' : '1px solid #d9d9d9',
                            borderRadius: '8px',
                            textAlign: 'left',
                            padding: '12px',
                            backgroundColor: selectedOption === FREETEXT_RADIO ? '#f0f9f9' : 'transparent',
                        }}
                    >
                        <Radio value={FREETEXT_RADIO} disabled={pickupSelectDisabled}>
                            <span style={{ fontWeight: 500, color: '#262626' }}>
                                <FormattedMessage
                                    id="checkout.pickup.enterManually"
                                    defaultMessage="Enter address or details manually"
                                />
                            </span>
                        </Radio>
                        {selectedOption === FREETEXT_RADIO && (
                            <div style={{ marginTop: '12px' }}>
                                <Input
                                    value={freetextValue}
                                    onChange={(e) => onChange(e.target.value, U_FREETEXT)}
                                    disabled={pickupSelectDisabled}
                                    placeholder={intl.formatMessage({
                                        id: 'checkout.pickup.freetextPlaceholder',
                                        defaultMessage: 'Type the pickup or meeting point details',
                                    })}
                                    style={{ height: 30 }}
                                />
                            </div>
                        )}
                    </div>
                )}

                {filteredothersPoints.map(p => {
                    const description = p.description ? p.description : p.reference;
                    return (
                        <div key={p.reference} className="option-item" style={{ border: selectedOption === p.reference ? '1px solid #008489' : '1px solid #d9d9d9', borderRadius: '8px', textAlign: 'left', padding: '12px', backgroundColor: selectedOption === p.reference ? '#f0f9f9' : 'transparent' }}>
                            <Radio value={p.reference}>
                                <span style={{ fontWeight: 500, color: '#262626' }}>
                                    {description === 'CONTACT_SUPPLIER_LATER' ? (
                                        <FormattedMessage id="checkout.pickup.contactSupplierLater" defaultMessage="I'll contact the supplier later" />
                                    ) : p.reference === 'MEET_AT_DEPARTURE_POINT' || description === 'MEET_AT_DEPARTURE_POINT' ? (
                                        <FormattedMessage id="checkout.pickup.meetAtDeparturePoint" defaultMessage="I will meet at the departure point." />
                                    ) : (
                                        description
                                    )}
                                </span>
                            </Radio>
                        </div>
                    );
                })}
            </Radio.Group>
        </div>
    );
};
